import { defineStore } from 'pinia'

const Static_vendor_id = 56;
const Static_base_url = 'https://v4.ozfoodz.com.au/api/';

const vendor_id = import.meta.env.VITE_VENDOR_ID ? parseInt(import.meta.env.VITE_VENDOR_ID) : Static_vendor_id;
const base_url = import.meta.env.VITE_BASE_URL || Static_base_url;

// Data expiration time in milliseconds (12 hours)
const DATA_EXPIRATION_MS = 12 * 60 * 60 * 1000;
// Separate localStorage key for tracking when data was stored
const DATA_STORED_AT_KEY = 'vendorDataStoredAt';

// Helper function to check if vendor data is valid
function isVendorDataValid(data) {
  if (!data || typeof data !== 'object') return false;
  // Check for essential required fields
  if (!data.id || !data.name) return false;
  return true;
}

// Helper function to check if workingDays data is valid
function isWorkingDaysValid(data) {
  if (!data || typeof data !== 'object') return false;
  // Check if it has the expected structure
  if (!data.selling_timeslot && !data.delivery_timeslot && !data.pickup_timeslot) {
    return false;
  }
  // Ensure at least one timeslot is an array
  const hasValidSlots = (
    Array.isArray(data.selling_timeslot) ||
    Array.isArray(data.delivery_timeslot) ||
    Array.isArray(data.pickup_timeslot)
  );
  return hasValidSlots;
}

// Get the stored timestamp from separate localStorage key
function getDataStoredAt() {
  try {
    const value = localStorage.getItem(DATA_STORED_AT_KEY);
    return value ? parseInt(value, 10) : null;
  } catch (e) {
    return null;
  }
}

// Set the stored timestamp to separate localStorage key
function setDataStoredAt(timestamp) {
  try {
    localStorage.setItem(DATA_STORED_AT_KEY, String(timestamp));
  } catch (e) {
    console.error('Failed to save dataStoredAt:', e);
  }
}

export const vendorStore = defineStore('vendor', {
  state: () => ({
    resturant: {},
    cateogries: [],
    // New structure: object with categorized timeslots
    workingDays: {
      selling_timeslot: [],
      delivery_timeslot: [],
      pickup_timeslot: []
    },
    tiffin_delivery_time: [], // DEPRECATED: No longer used, kept for backward compatibility
    tables: [],
    single_menue_products: [],
    delivery_charges: [],
    pages_statues: {},
    vender_id: vendor_id,
    base_url: base_url,
    resturantStatus: JSON.parse(localStorage.getItem('vendorStatus')),
    homePageURL: localStorage.getItem('homeUrl') || '/',
    globalLoading: false, // Control global app loader

  }),
  actions: {
    async vednorStorage(vendor) {
      this.resturant = vendor.data.vendor;

      // Handle both old (array) and new (object) structure for backward compatibility
      const wd = vendor.data.WorkDays;
      if (Array.isArray(wd)) {
        // Legacy format: flat array with type property
        this.workingDays = {
          selling_timeslot: wd.filter(d => d.type === 'selling_timeslot' || !d.type),
          delivery_timeslot: wd.filter(d => d.type === 'delivery_timeslot' || !d.type),
          pickup_timeslot: wd.filter(d => d.type === 'pickup_timeslot' || !d.type)
        };
      } else if (wd && typeof wd === 'object') {
        // New format: categorized object
        // API returns: selling_timeslot, delivery_time, pick_up_time
        this.workingDays = {
          selling_timeslot: wd.selling_timeslot || [],
          delivery_timeslot: wd.delivery_time || [],
          pickup_timeslot: wd.pick_up_time || []
        };
      }

      this.cateogries = vendor.data.MenuCategory;
      this.tables = vendor.data.tables;
      // tiffin_delivery_time removed - now using delivery_timeslot/pickup_timeslot
      this.delivery_charges = vendor.data.delivery_charges.charges;

      // Store timestamp in SEPARATE localStorage key (not part of Pinia persist)
      setDataStoredAt(Date.now());
    },
    async storePagesStatus(obj) {
      this.pages_statues = obj;
    },
    async vendorType(url) {
      this.homePageURL = url;
    },

    /**
     * Check if stored data is valid and not expired
     * Returns true if data is OK, false if refresh is needed
     */
    checkDataIntegrity() {
      // Get both keys
      const dataStoredAt = getDataStoredAt();
      const vendorLocalStorage = localStorage.getItem('vendor');

      // Debug logging - helps identify which branch triggers on issues
      console.log('📦 Integrity Check:', {
        hasTimestamp: !!dataStoredAt,
        hasVendorData: !!vendorLocalStorage,
        timestamp: dataStoredAt ? new Date(dataStoredAt).toISOString() : null
      });

      // Fresh install: neither key exists - skip check, let API load
      if (!dataStoredAt && !vendorLocalStorage) {
        console.log('📦 ✓ No stored data yet - skipping integrity check (fresh install)');
        return true;
      }

      // Mismatch: timestamp exists but vendor data doesn't - reload
      if (dataStoredAt && !vendorLocalStorage) {
        console.log('📦 ✗ Vendor localStorage key deleted. Clearing and reloading...');
        this.clearAndReload();
        return false;
      }

      // Mismatch: vendor data exists but timestamp doesn't
      if (!dataStoredAt && vendorLocalStorage) {
        try {
          // Check if it's just the default empty state (fresh install that persisted default state)
          const parsed = JSON.parse(vendorLocalStorage);
          const r = parsed.resturant;
          const isEmpty = !r || (Object.keys(r).length === 0 && r.constructor === Object) || (!r.id && !r.name);

          if (isEmpty) {
            console.log('📦 ✓ Vendor localStorage exists but is empty (default state) - allowing initial load');
            return true;
          }

          // GRACE PERIOD: If vendor data has content but no timestamp, this might be
          // a race condition on initial load. Set the timestamp now instead of reloading.
          console.log('📦 ⚠ Vendor data exists without timestamp. Setting timestamp now (grace period)...');
          setDataStoredAt(Date.now());
          return true;

        } catch (e) {
          console.log('📦 ✗ Invalid JSON in vendor localStorage. Clearing...');
          this.clearAndReload();
          return false;
        }
      }

      // Check if data is expired (older than 12 hours)
      const dataAge = Date.now() - dataStoredAt;
      const hoursOld = (dataAge / (1000 * 60 * 60)).toFixed(1);
      if (dataAge > DATA_EXPIRATION_MS) {
        console.log(`📦 ✗ Vendor data expired (${hoursOld} hours old, max 12 hours). Clearing...`);
        this.clearAndReload();
        return false;
      }

      // At this point both keys exist - continue with validation

      // Parse and validate the localStorage data
      try {
        const parsed = JSON.parse(vendorLocalStorage);
        const storedVendor = parsed.resturant;

        // Check if vendor data is empty/initial state (Pinia persisted default empty object)
        // If empty, this is a fresh state waiting for API data - allow it
        const isEmptyVendor = !storedVendor ||
          (Object.keys(storedVendor).length === 0) ||
          (!storedVendor.id && !storedVendor.name);

        if (isEmptyVendor) {
          console.log('📦 ✓ Vendor data is empty (initial state) - allowing API to populate');
          return true;
        }

        // Check if restaurant data is valid (only if it has data)
        if (!isVendorDataValid(storedVendor)) {
          console.log('📦 ✗ Vendor data corrupted or missing required fields (id, name). Clearing...');
          this.clearAndReload();
          return false;
        }

        // Check if workingDays data is valid
        const storedWorkingDays = parsed.workingDays;
        if (!isWorkingDaysValid(storedWorkingDays)) {
          console.log('📦 ✗ Working days data corrupted or missing. Clearing...');
          this.clearAndReload();
          return false;
        }
      } catch (e) {
        console.log('📦 ✗ Vendor data JSON parse error. Clearing...', e.message);
        this.clearAndReload();
        return false;
      }

      console.log(`📦 ✓ Data integrity OK (${hoursOld} hours old)`);
      return true;
    },

    /**
     * Clear all caches and perform a hard reload
     */
    clearAndReload() {
      console.log('📦 Performing complete cache clear and hard reload...');

      // 1. Clear ALL localStorage
      localStorage.clear();

      // 2. Clear ALL sessionStorage
      sessionStorage.clear();

      // 3. Clear ALL Cookies
      try {
        const cookies = document.cookie.split(";");
        for (let i = 0; i < cookies.length; i++) {
          const cookie = cookies[i];
          const eqPos = cookie.indexOf("=");
          const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
          document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
        }
      } catch (e) {
        console.error('Failed to clear cookies:', e);
      }

      // 4. Clear service worker caches if available
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => {
            caches.delete(name);
          });
        });
      }

      // 5. Unregister service workers if any
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then(registrations => {
          registrations.forEach(registration => {
            registration.unregister();
          });
        });
      }

      // 6. Hard reload bypassing browser cache
      // Using location.href = location.href with cache-busting parameter
      const url = new URL(window.location.href);
      url.searchParams.set('_refresh', Date.now());
      window.location.href = url.toString();
    }
  },
  persist: {
    paths: ['workingDays', 'resturant', 'cateogries', 'homePageURL', 'delivery_charges', 'pages_statues'],
  },
});
